import {
  concernOptionsFor,
  professionForTrack,
  trackOffersScenarios,
  type Profession,
} from "./constants";
import type { OnboardingDraftAnswers } from "./schema";

/** The guided onboarding's step order: the ONE place it lives. Components
 *  render a step; this module decides which step comes next, which steps a
 *  given user sees at all, and where a returning user resumes. Pure so every
 *  path (student, instructor, written-exam-only track, signed-in resume, lost
 *  draft) is unit-testable without a browser.
 *
 *  Two routes split the sequence at the submit:
 *  - `/onboarding` runs everything up to and including `about`, whose submit
 *    calls `record_onboarding` (consent + personal fields + all answers, in
 *    one transaction).
 *  - `/onboarding/finish` runs the post-submit steps (`paywall`, `firstCall`,
 *    `instructorHandoff`). They need a completed profile, so they can't share
 *    the pre-submit route, whose page sends onboarded users home. */
export const STEP_IDS = [
  "hook",
  "role",
  "profession",
  "track",
  "examDate",
  "concerns",
  "reframe",
  "howItWorks",
  "referral",
  "pace",
  "building",
  "plan",
  "account",
  "about",
  "paywall",
  "firstCall",
  "instructorHandoff",
] as const;
export type StepId = (typeof STEP_IDS)[number];

/** Steps rendered by `/onboarding/finish`, after `record_onboarding`. The one
 *  owner of that list: the wizard's pre-submit step type and its guard both
 *  derive from it. */
const POST_SUBMIT_STEP_IDS = [
  "paywall",
  "firstCall",
  "instructorHandoff",
] as const satisfies ReadonlyArray<StepId>;
export type PostSubmitStepId = (typeof POST_SUBMIT_STEP_IDS)[number];
const POST_SUBMIT_STEPS: ReadonlySet<StepId> = new Set(POST_SUBMIT_STEP_IDS);
export function isPostSubmitStep(step: StepId): step is PostSubmitStepId {
  return POST_SUBMIT_STEPS.has(step);
}

/** What the flow knows besides the answers. `profession` is UI-only state
 *  (it narrows the track list) and is never persisted server-side: the
 *  profession of a track is derived from its cert. */
export type FlowState = {
  answers: OnboardingDraftAnswers;
  profession?: Profession;
};

export type FlowContext = {
  /** A session exists, so the account step is skipped. */
  authed: boolean;
  /** Already entitled (paid / trialing): no paywall. Only known server-side
   *  on the finish route; pre-submit it is always false. */
  skipPaywall?: boolean;
};

/** Per-step presentation facts, so the wizard, the progress count and the
 *  navigation rules read one table instead of each listing step ids. */
const STEP_META: Partial<Record<StepId, { chromeless?: true; autoAdvance?: true }>> = {
  hook: { chromeless: true },
  building: { chromeless: true, autoAdvance: true },
};
/** Drawn without the back button and progress bar, and not counted. */
export function isChromeless(step: StepId): boolean {
  return STEP_META[step]?.chromeless === true;
}
/** Moves on by itself; never a target for back or resume. */
function autoAdvances(step: StepId): boolean {
  return STEP_META[step]?.autoAdvance === true;
}

/** The user's profession: the one they picked, else their track's. */
export function professionOf(s: FlowState): Profession | undefined {
  return s.profession ?? (s.answers.track ? professionForTrack(s.answers.track) : undefined);
}

/** The step each optional answer is collected on. An answer whose step is
 *  not in this user's sequence is dropped by `applicableAnswers`, so the
 *  "which steps does this user see" rule (stepsFor) has one owner. */
const ANSWER_STEP: Partial<Record<keyof OnboardingDraftAnswers, StepId>> = {
  exam_timeframe: "examDate",
  concerns: "concerns",
  weekly_scenario_goal: "pace",
  referral_source: "referral",
};

/** The answers that still apply to this user. An answer from a step the user
 *  no longer sees (they switched to Instructor, or to a written-exam-only
 *  track), a track from a profession they switched away from, or a worry
 *  their profession doesn't offer is dropped, so it can't reach the readback
 *  screens, the paywall headline, or `record_onboarding`. Applied on every
 *  change by the wizard, so state never disagrees with the visible steps. */
export function applicableAnswers(s: FlowState): OnboardingDraftAnswers {
  const a = { ...s.answers };
  if (a.track && s.profession && professionForTrack(a.track) !== s.profession) {
    delete a.track;
  }
  const visible = new Set(stepsFor({ ...s, answers: a }, { authed: false }));
  for (const key of Object.keys(ANSWER_STEP) as (keyof typeof ANSWER_STEP)[]) {
    if (!visible.has(ANSWER_STEP[key]!)) delete a[key];
  }
  const profession = professionOf({ ...s, answers: a });
  if (a.concerns && profession) {
    const offered = new Set<string>(concernOptionsFor(profession).map((o) => o.value));
    a.concerns = a.concerns.filter((c) => offered.has(c));
  }
  return a;
}

function isInstructor(s: FlowState): boolean {
  return s.answers.audience === "instructor";
}

/** Every step this user will see, in order. Before the role is picked the
 *  student path is assumed (it's the longer one, so the progress bar never
 *  jumps backward when "Student" is chosen). */
export function stepsFor(s: FlowState, ctx: FlowContext): StepId[] {
  const steps: StepId[] = ["hook", "role", "profession", "track"];
  if (isInstructor(s)) {
    steps.push("referral");
  } else {
    steps.push("examDate", "concerns", "reframe", "howItWorks", "referral");
    // The pace question is "scenarios per week": meaningless for a
    // written-exam-only track. Unknown track yet → keep it (conservative).
    if (!s.answers.track || trackOffersScenarios(s.answers.track)) {
      steps.push("pace");
    }
    steps.push("building", "plan");
  }
  if (!ctx.authed) steps.push("account");
  steps.push("about");
  if (isInstructor(s)) {
    steps.push("instructorHandoff");
  } else {
    if (!ctx.skipPaywall) steps.push("paywall");
    steps.push("firstCall");
  }
  return steps;
}

export function nextStep(
  current: StepId,
  s: FlowState,
  ctx: FlowContext,
): StepId | null {
  const steps = stepsFor(s, ctx);
  const i = steps.indexOf(current);
  // A step that isn't in this user's sequence (e.g. `pace` after switching
  // to CRT) moves forward to the first step after its canonical position.
  if (i === -1) return firstStepAfter(current, steps);
  return steps[i + 1] ?? null;
}

export function prevStep(
  current: StepId,
  s: FlowState,
  ctx: FlowContext,
): StepId | null {
  const steps = stepsFor(s, ctx);
  const i = steps.indexOf(current);
  if (i <= 0) return null;
  const prev = steps[i - 1]!;
  // Never walk back across the submit, and never back onto an auto-advancing
  // step (it would bounce the user forward again).
  if (isPostSubmitStep(current) && !isPostSubmitStep(prev)) return null;
  if (autoAdvances(prev)) return steps[i - 2] ?? null;
  return prev;
}

function firstStepAfter(step: StepId, steps: StepId[]): StepId | null {
  const order = STEP_IDS.indexOf(step);
  return steps.find((x) => STEP_IDS.indexOf(x) > order) ?? null;
}

/** The answer each step requires before any LATER step may render. */
const REQUIRED_BEFORE: ReadonlyArray<{
  step: StepId;
  satisfied: (s: FlowState) => boolean;
}> = [
  { step: "role", satisfied: (s) => Boolean(s.answers.audience) },
  {
    step: "profession",
    satisfied: (s) => Boolean(s.profession) || Boolean(s.answers.track),
  },
  { step: "track", satisfied: (s) => Boolean(s.answers.track) },
];

/** Where to put a user who arrives with a saved draft (or none).
 *
 *  - A saved step whose prerequisites are met wins: a refresh, or the return
 *    from an OAuth round-trip, lands exactly where they were.
 *  - `account` once signed in resolves to the step after it.
 *  - Otherwise the first required step still missing. This is also the
 *    lost-draft path: an email confirmed in another browser arrives signed in
 *    with an empty draft and is asked only what `record_onboarding` needs
 *    (role, then profession and track) before the optional questions.
 *  - Signed in with nothing saved skips the hook: it's a cold-open pitch for
 *    someone who hasn't signed up yet. */
export function resumeStep(
  saved: StepId | undefined,
  s: FlowState,
  ctx: FlowContext,
): StepId {
  const steps = stepsFor(s, ctx);
  const firstMissing = REQUIRED_BEFORE.find((r) => !r.satisfied(s));

  if (saved && !isPostSubmitStep(saved)) {
    // A saved step this user no longer sees (`account` once signed in, `pace`
    // after switching to a written-exam-only track) resolves to the next one
    // they do; the loader resolves to the plan it was building.
    let target: StepId = steps.includes(saved)
      ? saved
      : (firstStepAfter(saved, steps) ?? "about");
    if (autoAdvances(target)) target = nextStep(target, s, ctx) ?? target;
    // The hook is a cold-open pitch for someone without an account; a signed-in
    // user (back from sign-in with a draft saved on it) starts at the questions.
    if (target === "hook" && ctx.authed) target = "role";
    // Nothing past the account step renders without a session: a draft saved
    // on `about` (the user signed in, then signed out or their session lapsed)
    // resumes at sign-in, never on the personal-details form.
    const accountAt = steps.indexOf("account");
    if (!ctx.authed && accountAt !== -1 && steps.indexOf(target) > accountAt) {
      target = "account";
    }
    const targetAt = steps.indexOf(target);
    const blockedAt = firstMissing ? steps.indexOf(firstMissing.step) : -1;
    if (targetAt !== -1 && (blockedAt === -1 || targetAt <= blockedAt)) {
      return target;
    }
  }
  if (firstMissing) {
    // A brand-new anonymous visitor starts at the pitch, not a question.
    if (firstMissing.step === "role" && !ctx.authed && !saved) return "hook";
    return firstMissing.step;
  }
  // Everything required is answered but no usable saved step: go to the
  // step that turns answers into an account / a submit.
  return ctx.authed ? "about" : "account";
}

/** 1-based position and total for the progress bar. Hook and the loader are
 *  chrome-free, so they're excluded from both counts. */
export function progressFor(
  step: StepId,
  s: FlowState,
  ctx: FlowContext,
): { index: number; total: number } {
  const counted: StepId[] = stepsFor(s, ctx).filter((x) => !isChromeless(x));
  return { index: Math.max(0, counted.indexOf(step)) + 1, total: counted.length };
}
